// Automation Dashboard Component
// Main dashboard for managing automation decisions and approvals

import React, { useState, useEffect, useContext } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Divider
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Timeline as StatsIcon,
  PlayArrow as TestIcon,
  Science as ExperimentIcon,
  KeyboardArrowDown as ArrowDownIcon
} from '@mui/icons-material';

import EmptyState from '../common/EmptyState';
import BrandContext from '../../context/BrandContext';
import { useAuth } from '../../context/AuthContext';
import automationService from '../../services/automationService';

function AutomationDashboard() {
  // Context - with null check
  const brandContext = useContext(BrandContext);
  const { selectedBrand, brands, selectBrand, loading: brandsLoading } = brandContext || { 
    selectedBrand: null, 
    brands: [], 
    selectBrand: () => {}, 
    loading: false 
  };
  const { user } = useAuth();

  // State
  const [activeTab, setActiveTab] = useState(0);
  const [pendingDecisions, setPendingDecisions] = useState([]);
  const [allDecisions, setAllDecisions] = useState([]);
  const [automationStats, setAutomationStats] = useState({});
  const [automationSettings, setAutomationSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [selectedDecision, setSelectedDecision] = useState(null);
  const [approvalReason, setApprovalReason] = useState('');

  // Brand selection state
  const [brandMenuAnchor, setBrandMenuAnchor] = useState(null);
  const [selectedBrandForAutomation, setSelectedBrandForAutomation] = useState(null);

  // Get company ID from user context
  const companyId = user?.companyId || 1;
  const brandId = selectedBrandForAutomation?.id || selectedBrand?.id || null;

  // Load data on component mount and brand change
  useEffect(() => {
    if (companyId) {
      loadAllData();
    }
  }, [companyId, brandId]);

  // Load all data
  const loadAllData = async () => {
    setLoading(true);
    setError('');
    
    try {
      await Promise.all([
        loadPendingDecisions(),
        loadAllDecisions(),
        loadAutomationStats(),
        loadAutomationSettings()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load automation data');
    } finally {
      setLoading(false);
    }
  };

  // Load pending decisions
  const loadPendingDecisions = async () => {
    try {
      const response = await automationService.getPendingDecisions(companyId, brandId);
      if (response.success) {
        setPendingDecisions(response.decisions || []);
      }
    } catch (error) {
      console.error('Error loading pending decisions:', error);
    }
  };

  // Load all decisions
  const loadAllDecisions = async () => {
    try {
      const response = await automationService.getAllDecisions({
        companyId,
        brandId,
        limit: 50
      });
      if (response.success) {
        setAllDecisions(response.decisions || []);
      }
    } catch (error) {
      console.error('Error loading all decisions:', error);
    }
  };

  // Load automation stats
  const loadAutomationStats = async () => {
    try {
      const response = await automationService.getAutomationStats(companyId, brandId);
      if (response.success) {
        setAutomationStats(response.stats || {});
      }
    } catch (error) {
      console.error('Error loading automation stats:', error);
    }
  };

  // Load automation settings
  const loadAutomationSettings = async () => {
    try {
      const response = await automationService.getAutomationSettings(companyId);
      if (response.success) {
        setAutomationSettings(response.settings || {});
      }
    } catch (error) {
      console.error('Error loading automation settings:', error);
    }
  };

  // Handle decision approval/rejection
  const handleDecisionUpdate = async (decisionId, status, reason = null) => {
    try {
      const response = await automationService.updateDecisionStatus(decisionId, status, reason);
      if (response.success) {
        setSuccess(`Decision ${status} successfully`);
        await loadAllData(); // Refresh data
        setViewModalOpen(false);
        setApprovalReason('');
      } else {
        setError(response.message || `Failed to ${status} decision`);
      }
    } catch (error) {
      console.error(`Error ${status} decision:`, error);
      setError(`Failed to ${status} decision`);
    }
  };

  // Handle automation settings update
  const handleSettingsUpdate = async (newSettings) => {
    try {
      const response = await automationService.updateAutomationSettings(companyId, newSettings);
      if (response.success) {
        setSuccess('Automation settings updated successfully');
        await loadAutomationSettings();
        setSettingsModalOpen(false);
      } else {
        setError(response.message || 'Failed to update settings');
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      setError('Failed to update automation settings');
    }
  };

  // Handle manual test decision creation
  const handleCreateTestDecision = async () => {
    try {
      setLoading(true);
      const response = await automationService.createTestDecision();
      if (response.success) {
        setSuccess('Test automation decision created successfully!');
        await loadAllData();
      } else {
        setError(response.message || 'Failed to create test decision');
      }
    } catch (error) {
      console.error('Error creating test decision:', error);
      setError('Failed to create test decision');
    } finally {
      setLoading(false);
    }
  };

  // Handle brand selection
  const handleBrandSelect = (brand) => {
    setSelectedBrandForAutomation(brand);
    setBrandMenuAnchor(null);
  };

  // Get current brand name for display
  const getCurrentBrandName = () => {
    const currentBrand = selectedBrandForAutomation || selectedBrand;
    if (!currentBrand) return 'Select Brand';
    if (currentBrand.name === 'Company Level') return 'All Brands';
    return currentBrand.name;
  };

  // Format decision analysis data for display
  const formatAnalysisData = (data) => {
    if (!data) return 'No analysis data available';
    
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (e) {
        return data;
      }
    }

    const formatted = [];
    if (data.roas) formatted.push(`ROAS: ${data.roas}`);
    if (data.spend_efficiency) formatted.push(`Spend Efficiency: ${(data.spend_efficiency * 100).toFixed(1)}%`);
    if (data.trend) formatted.push(`Trend: ${data.trend}`);
    if (data.reasoning) formatted.push(`Reasoning: ${data.reasoning}`);
    
    return formatted.length > 0 ? formatted.join(' • ') : JSON.stringify(data, null, 2);
  };

  // Format recommended action for display
  const formatRecommendedAction = (action) => {
    if (!action) return 'No action details available';
    
    if (typeof action === 'string') {
      try {
        action = JSON.parse(action);
      } catch (e) {
        return action;
      }
    }

    const formatted = [];
    if (action.action) formatted.push(`Action: ${automationService.formatDecisionType(action.action)}`);
    if (action.amount) formatted.push(`Amount: $${action.amount}`);
    if (action.percentage) formatted.push(`Change: ${action.percentage}%`);
    if (action.campaign_ids) formatted.push(`Campaigns: ${action.campaign_ids.length} affected`);
    if (action.description) formatted.push(`Details: ${action.description}`);
    
    return formatted.length > 0 ? formatted.join(' • ') : JSON.stringify(action, null, 2);
  };

  // Stats Cards Component
  const StatsCards = () => (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={6} md={3}>
        <Card className="card-hover">
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" className="text-gradient">
                  {automationStats.total || 0}
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  Total Decisions
                </Typography>
              </Box>
              <StatsIcon sx={{ fontSize: 40, color: 'var(--color-primary)', opacity: 0.7 }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <Card className="card-hover">
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" className="text-gradient">
                  {automationStats.pending || 0}
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  Pending Approval
                </Typography>
              </Box>
              <Box sx={{ color: '#f59e0b', fontSize: 40 }}>⏳</Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <Card className="card-hover">
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" className="text-gradient">
                  {automationStats.approved || 0}
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  Approved
                </Typography>
              </Box>
              <ApproveIcon sx={{ fontSize: 40, color: '#10b981', opacity: 0.7 }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <Card className="card-hover">
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" className="text-gradient">
                  {automationStats.executed || 0}
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  Executed
                </Typography>
              </Box>
              <Box sx={{ color: '#06b6d4', fontSize: 40 }}>🚀</Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  // Decision Card Component
  const DecisionCard = ({ decision, isPending = false }) => (
    <Card className="card-hover" sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              {automationService.formatDecisionType(decision.decision_type)}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Decision #{decision.id} • Brand {decision.brand_id}
            </Typography>
          </Box>
          <Chip
            label={decision.status}
            color={decision.status === 'approved' ? 'success' : 
                   decision.status === 'rejected' ? 'error' : 'warning'}
            size="small"
          />
        </Box>

        <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
          <strong>Analysis:</strong> {formatAnalysisData(decision.analysis_data)}
        </Typography>

        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          <strong>Action:</strong> {formatRecommendedAction(decision.recommended_action)}
        </Typography>

        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
          Created: {new Date(decision.created_at).toLocaleString()}
        </Typography>

        <Box display="flex" gap={1}>
          <Button
            size="small"
            startIcon={<ViewIcon />}
            onClick={() => {
              setSelectedDecision(decision);
              setError(''); setViewModalOpen(true);
            }}
          >
            View Details
          </Button>
          
          {isPending && (
            <>
              <Button
                size="small"
                color="success"
                startIcon={<ApproveIcon />}
                onClick={() => handleDecisionUpdate(decision.id, 'approved')}
              >
                Approve
              </Button>
              <Button
                size="small"
                color="error"
                startIcon={<RejectIcon />}
                onClick={() => handleDecisionUpdate(decision.id, 'rejected')}
              >
                Reject
              </Button>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  // Settings Modal
  const SettingsModal = () => (
    <Dialog open={settingsModalOpen} onClose={() => setSettingsModalOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>Automation Settings</DialogTitle>
      <DialogContent>
        {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={automationSettings.automationEnabled || false}
                onChange={(e) => 
                  handleSettingsUpdate({ 
                    automationEnabled: e.target.checked,
                    automationSettings: automationSettings.automationSettings 
                  })
                }
              />
            }
            label="Enable Automation for this Company"
          />
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
            When enabled, n8n workflows can create automation decisions for approval.
          </Typography>

          {automationSettings.automationEnabled && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Automation is currently enabled. n8n workflows will analyze your data and create
              optimization recommendations that require approval before execution.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setSettingsModalOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  // Decision Details Modal
  const DecisionDetailsModal = () => (
    <Dialog open={viewModalOpen} onClose={() => setViewModalOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        Decision Details #{selectedDecision?.id}
      </DialogTitle>
      <DialogContent>
        {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {selectedDecision && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>Type</Typography>
                <Typography variant="body2" gutterBottom>
                  {automationService.formatDecisionType(selectedDecision.decision_type)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>Status</Typography>
                <Chip
                  label={selectedDecision.status}
                  color={selectedDecision.status === 'approved' ? 'success' : 
                         selectedDecision.status === 'rejected' ? 'error' : 'warning'}
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" gutterBottom sx={{ mt: 3 }}>
              Analysis Data
            </Typography>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
              <pre style={{ fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>
                {JSON.stringify(selectedDecision.analysis_data, null, 2)}
              </pre>
            </Box>

            <Typography variant="subtitle2" gutterBottom>
              Recommended Action
            </Typography>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 3 }}>
              <pre style={{ fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>
                {JSON.stringify(selectedDecision.recommended_action, null, 2)}
              </pre>
            </Box>

            {selectedDecision.status === 'pending' && (
              <>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Approval Reason (Optional)"
                  value={approvalReason}
                  onChange={(e) => setApprovalReason(e.target.value)}
                  sx={{ mb: 2 }}
                />
                
                <Box display="flex" gap={2}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<ApproveIcon />}
                    onClick={() => handleDecisionUpdate(selectedDecision.id, 'approved', approvalReason)}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<RejectIcon />}
                    onClick={() => handleDecisionUpdate(selectedDecision.id, 'rejected', approvalReason)}
                  >
                    Reject
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setViewModalOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" className="text-gradient" gutterBottom>
            🤖 Automation Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage automation decisions and approval workflows
          </Typography>
        </Box>
        
        <Box display="flex" gap={1} alignItems="center">
          {/* Brand Selector */}
          <Button
            variant="outlined"
            endIcon={<ArrowDownIcon />}
            onClick={(e) => setBrandMenuAnchor(e.currentTarget)}
            sx={{ minWidth: 150 }}
          >
            {getCurrentBrandName()}
          </Button>
          <Menu
            anchorEl={brandMenuAnchor}
            open={Boolean(brandMenuAnchor)}
            onClose={() => setBrandMenuAnchor(null)}
          >
            <MenuItem onClick={() => handleBrandSelect(null)}>
              All Brands
            </MenuItem>
            <Divider />
            {brands.map((brand) => (
              <MenuItem key={brand.id} onClick={() => handleBrandSelect(brand)}>
                {brand.name}
              </MenuItem>
            ))}
          </Menu>

          {/* Test Controls */}
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ExperimentIcon />}
            onClick={handleCreateTestDecision}
            disabled={loading}
          >
            Create Test
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => { setError(''); setSettingsModalOpen(true); }}
          >
            Settings
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={loadAllData}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      <StatsCards />

      {/* Content Tabs */}
      <Card>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={`Pending Decisions (${pendingDecisions.length})`} />
          <Tab label={`All Decisions (${allDecisions.length})`} />
        </Tabs>

        <CardContent>
          {/* Pending Decisions Tab */}
          {activeTab === 0 && (
            <Box>
              {pendingDecisions.length > 0 ? (
                pendingDecisions.map((decision) => (
                  <DecisionCard key={decision.id} decision={decision} isPending={true} />
                ))
              ) : (
                <Box textAlign="center" py={4}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No pending decisions
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    All automation decisions have been processed.
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* All Decisions Tab */}
          {activeTab === 1 && (
            <Box>
              {allDecisions.length > 0 ? (
                allDecisions.map((decision) => (
                  <DecisionCard key={decision.id} decision={decision} />
                ))
              ) : (
                <EmptyState
                  compact
                  title="No decisions found"
                  description="Automation decisions will appear here once n8n workflows create them."
                />
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <SettingsModal />
      <DecisionDetailsModal />
    </Box>
  );
}

export default AutomationDashboard;
